import { useState, useMemo } from 'react';
import { ArrowLeftRight, Pencil, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMutation } from '@apollo/client';
import { useFindServicePackageForStoreQuery } from '@/graphql/queries/findServicePackageForStore.generated';
import { useSetStoreServicePackageMutation } from '@/graphql/mutations/setStoreServicePackage.generated';
import { useSaveServicePackageRevisionMutation } from '@/graphql/mutations/saveServicePackageRevision.generated';
import { useServicePackagesQuery } from '@/graphql/queries/servicePackages.generated';
import {
  ServicePackageType,
  ContainerTypeSegment,
} from '@/graphql/generatedTypes';
import { updateStoreSuperAdminMutation } from '../../../graphql/mutations';
import type {
  RevisionFormState,
  ServicePackageRevision,
} from '../../ServicePackagePlans/types';
import {
  PACKAGE_TYPE_LABELS,
  SEGMENT_LABELS,
  latestRevisionForTerm,
  uniqueTerms,
  formatCents,
  revisionToForm,
  emptyRevisionForm,
} from '../../ServicePackagePlans/types';
import { ServicePackageRevisionDialog } from '../../ServicePackagePlans/components/ServicePackageRevisionDialog';
import i18n from '@/utils/i18n';

export function ServicePackageTab({
  storeId,
  storePickpackId,
  currency,
  invoiceYearly,
  onboardedDate,
  servicePackageStartDate,
  freeCheckoutSegment,
  sustainabilityReportsEnabled,
}: {
  storeId: string;
  storePickpackId: string;
  currency: string;
  invoiceYearly: boolean | null;
  onboardedDate: string | null;
  servicePackageStartDate: string | null;
  freeCheckoutSegment: ContainerTypeSegment | null;
  sustainabilityReportsEnabled: boolean;
}) {
  // ── Current assignment ─────────────────────────────────────────────────
  const {
    data: assignedData,
    loading: assignedLoading,
    refetch,
  } = useFindServicePackageForStoreQuery({
    variables: { store_uuid: storeId },
    skip: !storeId,
  });
  const assignedPackage = assignedData?.findServicePackageForStore ?? null;

  // ServicePackageTerm exposes a single active revision directly
  const currentRevision: ServicePackageRevision | null =
    assignedPackage?.revision ?? null;

  // ── Edit revision dialog ───────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] =
    useState<RevisionFormState>(emptyRevisionForm());
  const [editSaving, setEditSaving] = useState(false);

  const [saveRevision] = useSaveServicePackageRevisionMutation();

  const openEdit = () => {
    if (!currentRevision) return;
    const full = revisionToForm(currentRevision);
    // In single-segment mode, filter to only the store's segment
    if (freeCheckoutSegment) {
      const match = full.free_checkouts.find(
        (fc) => fc.segment === freeCheckoutSegment,
      );
      full.free_checkouts = match
        ? [match]
        : [{ segment: freeCheckoutSegment, amount: '0' }];
    }
    setEditForm(full);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!assignedPackage || !currentRevision) return;
    setEditSaving(true);
    try {
      // With store_uuid, backend allows max 1 free_checkout entry and
      // automatically sets the store's FreeCheckoutSegment from it.
      await saveRevision({
        variables: {
          input: {
            currency: assignedPackage.currency,
            package_type: assignedPackage.package_type as ServicePackageType,
            min_contract_term_months: currentRevision.min_contract_term_months,
            monthly_service_fee_in_cents: parseInt(
              editForm.monthly_service_fee_in_cents,
              10,
            ),
            onboarding_fee_in_cents: parseInt(
              editForm.onboarding_fee_in_cents,
              10,
            ),
            ops_order_price_in_cents: parseInt(
              editForm.ops_order_price_in_cents,
              10,
            ),
            free_checkouts: editForm.free_checkouts
              .filter((fc) => fc.segment && parseInt(fc.amount, 10) >= 0)
              .map((fc) => ({
                segment: fc.segment,
                amount: parseInt(fc.amount, 10),
              })),
            note: editForm.note || undefined,
            allowances: editForm.asset_allowances.map((a) => ({
              item_type_id: String(a.item_type_id),
              monthly_quantity: parseInt(a.included_amount, 10),
            })),
          },
          store_uuid: storeId,
        },
        // Backend sets store's FreeCheckoutSegment, so refetch both
        refetchQueries: ['findServicePackageForStore', 'store'],
      });

      await refetch();
      setEditOpen(false);
    } finally {
      setEditSaving(false);
    }
  };

  // ── Reassign dialog ────────────────────────────────────────────────────
  const [setPackage] = useSetStoreServicePackageMutation();
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignSaving, setReassignSaving] = useState(false);
  const [selPackageType, setSelPackageType] = useState('');
  const [selRevisionId, setSelRevisionId] = useState('');
  // '' = not yet chosen; 'monthly' | 'annually' = chosen (no default)
  const [selBillingCycle, setSelBillingCycle] = useState<
    '' | 'monthly' | 'annually'
  >('');
  const [selFreeCheckoutSegment, setSelFreeCheckoutSegment] = useState('');
  const [selServicePackageStartDate, setSelServicePackageStartDate] =
    useState('');
  const [selSendSustainabilityReports, setSelSendSustainabilityReports] =
    useState(true);

  const [updateStore] = useMutation(updateStoreSuperAdminMutation);

  const { data: allPackagesData } = useServicePackagesQuery({
    skip: !reassignOpen,
  });
  const packagesForCurrency = useMemo(
    () =>
      (allPackagesData?.servicePackages ?? []).filter(
        (p) => p.currency === currency,
      ),
    [allPackagesData, currency],
  );

  const selectedPackage = useMemo(
    () =>
      packagesForCurrency.find((p) => p.package_type === selPackageType) ??
      null,
    [packagesForCurrency, selPackageType],
  );

  const availableTerms = useMemo(
    () => (selectedPackage ? uniqueTerms(selectedPackage.revisions) : []),
    [selectedPackage],
  );

  const previewRevision: ServicePackageRevision | null = useMemo(() => {
    if (!selRevisionId) return null;
    return (
      selectedPackage?.revisions.find((r) => r.id === selRevisionId) ?? null
    );
  }, [selectedPackage, selRevisionId]);

  // Default start date: existing service_package_start_date, else onboarded_date
  const defaultStartDate = (() => {
    const iso = servicePackageStartDate ?? onboardedDate;
    if (!iso) return '';
    // Convert ISO string to YYYY-MM-DD for date input
    return iso.slice(0, 10);
  })();

  const openReassign = () => {
    setSelPackageType('');
    setSelRevisionId('');
    setSelBillingCycle('');
    setSelFreeCheckoutSegment(freeCheckoutSegment ?? '');
    setSelServicePackageStartDate(defaultStartDate);
    setSelSendSustainabilityReports(sustainabilityReportsEnabled);
    setReassignOpen(true);
  };

  const handlePackageTypeChange = (v: string) => {
    setSelPackageType(v);
    setSelRevisionId('');
    setSelFreeCheckoutSegment('');
  };

  const handleTermChange = (termStr: string) => {
    const term = parseInt(termStr, 10);
    const rev = selectedPackage
      ? (latestRevisionForTerm(selectedPackage.revisions, term) ?? null)
      : null;
    setSelRevisionId(rev?.id ?? '');
    setSelFreeCheckoutSegment('');
  };

  const handleReassign = async () => {
    if (
      !selRevisionId ||
      !selBillingCycle ||
      !selFreeCheckoutSegment ||
      !selServicePackageStartDate
    )
      return;
    setReassignSaving(true);
    try {
      await Promise.all([
        setPackage({
          variables: {
            input: {
              store_uuid: storeId,
              service_package_revision_id: selRevisionId,
              free_checkout_segment:
                selFreeCheckoutSegment as ContainerTypeSegment,
              service_package_start_date:
                selServicePackageStartDate || undefined,
            },
          },
          refetchQueries: ['store'],
        }),
        updateStore({
          variables: {
            id: storePickpackId,
            store: {
              invoice_yearly: selBillingCycle === 'annually',
              sustainability_reports_enabled: selSendSustainabilityReports,
            },
          },
        }),
      ]);
      await refetch();
      setReassignOpen(false);
    } finally {
      setReassignSaving(false);
    }
  };

  const selectedTermStr = previewRevision
    ? String(previewRevision.min_contract_term_months)
    : '';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="tw:flex tw:flex-col tw:gap-6 tw:w-full tw:max-w-[872px]">
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:justify-end">
          {!assignedPackage && !assignedLoading && (
            <Button variant="outline" onClick={openReassign}>
              <Plus className="tw:size-4" />
              {i18n.t('ServicePackage:setServicePackage')}
            </Button>
          )}
        </div>

        <Card className="tw:p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="tw:pl-4 tw:h-13">
                  {i18n.t('ServicePackage:package')}
                </TableHead>
                <TableHead>{i18n.t('ServicePackage:term')}</TableHead>
                <TableHead>{i18n.t('ServicePackage:billing')}</TableHead>
                <TableHead>{i18n.t('ServicePackage:monthlyFee')}</TableHead>
                <TableHead>{i18n.t('ServicePackage:onboarding')}</TableHead>
                <TableHead>{i18n.t('ServicePackage:opsPrice')}</TableHead>
                <TableHead className="tw:w-[90px]">
                  {i18n.t('ServicePackage:freeCheckouts')}
                </TableHead>
                <TableHead className="tw:w-[72px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {!assignedPackage || !currentRevision ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="tw:pl-4 tw:py-8 tw:text-sm tw:text-muted-foreground"
                  >
                    {assignedLoading
                      ? i18n.t('Common:loading')
                      : i18n.t('ServicePackage:noPackageSelected')}
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow className="tw:*:first:pl-4 tw:*:last:pr-3">
                  <TableCell>
                    <span className="tw:text-sm tw:font-medium">
                      {PACKAGE_TYPE_LABELS[assignedPackage.package_type]}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="tw:text-sm">
                      {i18n.t('ServicePackage:nMonths', {
                        n: currentRevision.min_contract_term_months,
                      })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="tw:text-sm">
                      {invoiceYearly === true
                        ? i18n.t('ServicePackage:annually')
                        : invoiceYearly === false
                          ? i18n.t('ServicePackage:monthly')
                          : '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="tw:text-sm">
                      {formatCents(
                        currentRevision.monthly_service_fee_in_cents,
                        assignedPackage.currency,
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="tw:text-sm">
                      {formatCents(
                        currentRevision.onboarding_fee_in_cents,
                        assignedPackage.currency,
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="tw:text-sm">
                      {formatCents(
                        currentRevision.ops_order_price_in_cents,
                        assignedPackage.currency,
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      if (!freeCheckoutSegment) {
                        return (
                          <span className="tw:text-xs tw:text-muted-foreground">
                            {i18n.t('ServicePackage:noSegment')}
                          </span>
                        );
                      }
                      const storeCheckout = currentRevision.free_checkouts.find(
                        (fc) => fc.segment === freeCheckoutSegment,
                      );
                      const amount = storeCheckout?.amount ?? 0;
                      return (
                        <Badge
                          variant="secondary"
                          className="tw:text-xs tw:font-normal"
                        >
                          {SEGMENT_LABELS[freeCheckoutSegment]}: {amount}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="tw:flex tw:gap-1">
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={openEdit}
                        title={i18n.t('ServicePackage:editRevision')}
                      >
                        <Pencil className="tw:size-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={openReassign}
                        title={i18n.t('ServicePackage:reassignPackage')}
                      >
                        <ArrowLeftRight className="tw:size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Read-only start date display */}
        {assignedPackage && servicePackageStartDate && (
          <div className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground">
            <span>{i18n.t('ServicePackage:startDate')}</span>
            <span className="tw:font-medium tw:text-foreground">
              {new Date(servicePackageStartDate).toLocaleDateString('de-DE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
        )}
      </div>

      {/* Edit revision dialog — same as detail page */}
      {editOpen && assignedPackage && currentRevision && (
        <ServicePackageRevisionDialog
          open
          onOpenChange={(o) => !o && !editSaving && setEditOpen(false)}
          term={currentRevision.min_contract_term_months}
          isFirstRevision={false}
          form={editForm}
          onChange={setEditForm}
          onSave={handleEditSave}
          saving={editSaving}
          currency={assignedPackage.currency}
          singleSegment
        />
      )}

      {/* Reassign package dialog */}
      <Dialog
        open={reassignOpen}
        onOpenChange={(o) => !reassignSaving && setReassignOpen(o)}
      >
        <DialogContent className="tw:max-w-sm tw:max-h-[90vh] tw:flex tw:flex-col">
          <DialogHeader>
            <DialogTitle>
              {assignedPackage
                ? i18n.t('ServicePackage:reassignServicePackage')
                : i18n.t('ServicePackage:setServicePackage')}
            </DialogTitle>
          </DialogHeader>

          <div className="tw:flex tw:flex-col tw:gap-4 tw:py-2 tw:overflow-y-auto">
            {/* Currency — static, derived from store */}
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label>{i18n.t('Common:currency')}</Label>
              <div className="tw:flex tw:items-center tw:h-9 tw:px-3 tw:rounded-md tw:border tw:bg-muted tw:text-sm tw:text-muted-foreground">
                <Badge variant="outline" className="tw:font-mono">
                  {currency}
                </Badge>
              </div>
            </div>

            {/* Step 1 — Package type */}
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label>{i18n.t('ServicePackage:packageType')}</Label>
              <Select
                value={selPackageType}
                onValueChange={handlePackageTypeChange}
                disabled={packagesForCurrency.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      packagesForCurrency.length === 0
                        ? i18n.t('ServicePackage:noPackagesForCurrency', {
                            currency,
                          })
                        : i18n.t('ServicePackage:selectPackage')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {packagesForCurrency.map((p) => (
                    <SelectItem key={p.id} value={p.package_type}>
                      {PACKAGE_TYPE_LABELS[p.package_type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 2 — Contract term */}
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label>{i18n.t('ServicePackage:contractTerm')}</Label>
              <Select
                value={selectedTermStr}
                onValueChange={handleTermChange}
                disabled={!selPackageType || availableTerms.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={i18n.t('ServicePackage:selectTerm')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableTerms.map((t) => (
                    <SelectItem key={t} value={String(t)}>
                      {i18n.t('ServicePackage:nMonths', { n: t })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 3 — Billing cycle */}
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label>{i18n.t('ServicePackage:billingCycle')}</Label>
              <Select
                value={selBillingCycle}
                onValueChange={(v) =>
                  setSelBillingCycle(v as 'monthly' | 'annually')
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={i18n.t('ServicePackage:selectBillingCycle')}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">
                    {i18n.t('ServicePackage:monthly')}
                  </SelectItem>
                  <SelectItem value="annually">
                    {i18n.t('ServicePackage:annually')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Step 4 — Free checkout segment */}
            {(() => {
              const allSegments = Object.keys(
                SEGMENT_LABELS,
              ) as ContainerTypeSegment[];
              const selectedAmount = previewRevision
                ? (previewRevision.free_checkouts.find(
                    (fc) => fc.segment === selFreeCheckoutSegment,
                  )?.amount ?? null)
                : null;

              return (
                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <Label>{i18n.t('ServicePackage:freeCheckoutSegment')}</Label>
                  <Select
                    value={selFreeCheckoutSegment}
                    onValueChange={setSelFreeCheckoutSegment}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={i18n.t('ServicePackage:selectSegment')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {allSegments.map((s) => {
                        const amt = previewRevision?.free_checkouts.find(
                          (fc) => fc.segment === s,
                        )?.amount;
                        return (
                          <SelectItem key={s} value={s}>
                            {SEGMENT_LABELS[s]}
                            {amt != null
                              ? ' ' + i18n.t('ServicePackage:nFree', { n: amt })
                              : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {selFreeCheckoutSegment && previewRevision && (
                    <p className="tw:text-xs tw:text-muted-foreground">
                      {selectedAmount != null
                        ? i18n.t('ServicePackage:freeCheckoutsPerMonth', {
                            n: selectedAmount,
                            segment:
                              SEGMENT_LABELS[
                                selFreeCheckoutSegment as ContainerTypeSegment
                              ],
                          })
                        : i18n.t('ServicePackage:noFreeCheckoutsForSegment', {
                            segment:
                              SEGMENT_LABELS[
                                selFreeCheckoutSegment as ContainerTypeSegment
                              ],
                          })}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Step 5 — Service package start date */}
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label htmlFor="sp-start-date">
                {i18n.t('ServicePackage:servicePackageStartDate')}
              </Label>
              <Input
                id="sp-start-date"
                type="date"
                value={selServicePackageStartDate}
                onChange={(e) => setSelServicePackageStartDate(e.target.value)}
              />
            </div>

            {/* Step 6 — Sustainability reports */}
            <div className="tw:flex tw:items-center tw:gap-2">
              <Checkbox
                id="sp-send-sustainability-reports"
                checked={selSendSustainabilityReports}
                onCheckedChange={(v) =>
                  setSelSendSustainabilityReports(v === true)
                }
              />
              <Label htmlFor="sp-send-sustainability-reports">
                {i18n.t('SustainabilityReports:sendReports')}
              </Label>
            </div>

            {/* Preview card */}
            {previewRevision && selectedPackage && (
              <div className="tw:rounded-md tw:border tw:divide-y tw:text-sm">
                {[
                  [
                    i18n.t('ServicePackage:monthlyFee'),
                    formatCents(
                      previewRevision.monthly_service_fee_in_cents,
                      selectedPackage.currency,
                    ),
                  ],
                  [
                    i18n.t('ServicePackage:onboarding'),
                    formatCents(
                      previewRevision.onboarding_fee_in_cents,
                      selectedPackage.currency,
                    ),
                  ],
                  [
                    i18n.t('ServicePackage:opsOrderPrice'),
                    formatCents(
                      previewRevision.ops_order_price_in_cents,
                      selectedPackage.currency,
                    ),
                  ],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="tw:flex tw:items-center tw:justify-between tw:px-3 tw:py-2"
                  >
                    <span className="tw:text-muted-foreground">{label}</span>
                    <span className="tw:font-medium">{value}</span>
                  </div>
                ))}
                {/* Free checkouts per segment */}
                <div className="tw:px-3 tw:py-2">
                  <span className="tw:text-muted-foreground">
                    {i18n.t('ServicePackage:freeCheckouts')}
                  </span>
                  {previewRevision.free_checkouts.length > 0 ? (
                    <div className="tw:flex tw:flex-wrap tw:gap-1 tw:mt-1">
                      {previewRevision.free_checkouts.map((fc) => (
                        <Badge
                          key={fc.segment}
                          variant={
                            fc.segment === selFreeCheckoutSegment
                              ? 'default'
                              : 'secondary'
                          }
                          className="tw:text-xs tw:font-normal"
                        >
                          {SEGMENT_LABELS[fc.segment]}: {fc.amount}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="tw:ml-2 tw:font-medium">—</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReassignOpen(false)}
              disabled={reassignSaving}
            >
              {i18n.t('Common:cancel')}
            </Button>
            <Button
              onClick={handleReassign}
              disabled={
                !selRevisionId ||
                !selBillingCycle ||
                !selFreeCheckoutSegment ||
                !selServicePackageStartDate ||
                reassignSaving
              }
            >
              {reassignSaving
                ? i18n.t('Common:saving')
                : i18n.t('ServicePackage:assign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
